export const financeOperations = {
  slug: 'finance-operations',
  crumb: 'Finance operations',
  docTitle: 'Finance Operations Automation — Reconciliation & Reporting | methodic',
  intro: {
    label: 'Finance Operations',
    line1: 'Finance operations,',
    line2: 'made more reliable.',
    desc: 'Reduce repetitive reconciliation and reporting work by connecting financial data, surfacing exceptions and keeping critical review with your team.',
    meta: [
      { k: 'Scope', v: 'Reconciliation + Reporting' },
      { k: 'Systems', v: 'Finance tools + Spreadsheets' },
      { k: 'Control', v: 'Exception review' },
    ],
  },
  friction: {
    title: 'Where financial work slows down.',
    items: [
      { index: '01', title: 'Repeated reconciliation', desc: 'Teams compare transactions and records across exports by hand.' },
      { index: '02', title: 'Fragmented reporting', desc: 'Financial information must be assembled from multiple tools and sheets.' },
      { index: '03', title: 'Exception chasing', desc: 'Unmatched entries wait on manual investigation and follow-up.' },
    ],
  },
  system: {
    label: 'Reconciliation workbench',
    title: 'Matching records. Surfacing exceptions.',
    note: 'Matched pairs are verified. Unmatched items stay visible for review.',
  },
  implement: {
    title: 'More clarity across financial workflows.',
    items: [
      { index: '01', title: 'Data Collection & Mapping', desc: 'Bring recurring financial inputs into a consistent structure.' },
      { index: '02', title: 'Reconciliation & Exceptions', desc: 'Automate repeatable comparisons and route mismatches for review.' },
      { index: '03', title: 'Reporting & Oversight', desc: 'Prepare clearer recurring outputs with traceable review points.' },
    ],
  },
  consult: {
    title: 'Start with one finance workflow.',
    desc: 'Show us the reconciliation or reporting work that takes too much coordination.',
  },
};

export const salesOperations = {
  slug: 'sales-operations',
  crumb: 'Sales operations',
  docTitle: 'Sales Operations Automation — Lead Intake & Handoffs | methodic',
  intro: {
    label: 'Sales Operations',
    line1: 'Sales operations,',
    line2: 'with clearer handoffs.',
    desc: 'Connect lead intake, qualification and owner assignment so promising conversations move forward without losing context.',
    meta: [
      { k: 'Scope', v: 'Lead intake → Handoff' },
      { k: 'Systems', v: 'Forms + CRM + Team tools' },
      { k: 'Control', v: 'Ownership + Review' },
    ],
  },
  friction: {
    title: 'Where leads lose momentum.',
    items: [
      { index: '01', title: 'Scattered lead intake', desc: 'New inquiries reach different inboxes, forms and channels.' },
      { index: '02', title: 'Delayed qualification', desc: 'Teams spend time screening and sorting information manually.' },
      { index: '03', title: 'Unclear ownership', desc: 'Leads wait between systems before someone takes the next step.' },
    ],
  },
  system: {
    label: 'Lead handoff pipeline',
    title: 'From new inquiry to owned next step.',
    note: 'Every lead reaches a clear owner. Ambiguous leads stay in review.',
  },
  implement: {
    title: 'A cleaner path from inquiry to action.',
    items: [
      { index: '01', title: 'Lead Intake & Normalization', desc: 'Bring incoming inquiries into a consistent queue.' },
      { index: '02', title: 'Qualification & Prioritization', desc: 'Structure signals so teams know what needs attention.' },
      { index: '03', title: 'Ownership & CRM Updates', desc: 'Route next steps to the right team while preserving context.' },
    ],
  },
  consult: {
    title: 'Start with one sales workflow.',
    desc: 'Tell us where leads slow down between inquiry, qualification and follow-up.',
  },
};

export const internalWorkflows = {
  slug: 'internal-workflows',
  crumb: 'Internal workflows',
  docTitle: 'Internal Workflow Automation — Requests & Approvals | methodic',
  intro: {
    label: 'Internal Workflows',
    line1: 'Internal workflows,',
    line2: 'without the bottlenecks.',
    desc: 'Organize requests, approvals and routine coordination across teams while keeping decisions accountable and visible.',
    meta: [
      { k: 'Scope', v: 'Requests + Approvals' },
      { k: 'Systems', v: 'Workspaces + Internal tools' },
      { k: 'Control', v: 'Human sign-off' },
    ],
  },
  friction: {
    title: 'Where internal work gets held up.',
    items: [
      { index: '01', title: 'Scattered requests', desc: 'Work arrives through messages, forms and informal conversations.' },
      { index: '02', title: 'Approval bottlenecks', desc: 'Requests wait on unclear or repeated decision steps.' },
      { index: '03', title: 'Limited visibility', desc: 'Teams struggle to see ownership, status and outstanding actions.' },
    ],
  },
  system: {
    label: 'Approval gate',
    title: 'Every request has a clear next step.',
    note: 'Rules check routine work. Sign-off stays with the responsible person.',
  },
  implement: {
    title: 'Repeatable coordination, clearer ownership.',
    items: [
      { index: '01', title: 'Request Intake & Rules', desc: 'Structure incoming work and define consistent decision paths.' },
      { index: '02', title: 'Approvals & Exceptions', desc: 'Put sign-offs and ambiguous cases in the right hands.' },
      { index: '03', title: 'Updates & Audit Trail', desc: 'Keep outcomes and outstanding work visible across tools.' },
    ],
  },
  consult: {
    title: 'Start with one internal workflow.',
    desc: 'Show us an approval or recurring task that slows your team down.',
  },
};